import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Loader2, CreditCard, Check, AlertTriangle, Receipt, ExternalLink, Zap, X } from "lucide-react";
import { toast } from "../components/ui/sonner";
import { PageHeader } from "../components/ui/page-shell";
import { useAuth } from "../context/AuthContext";


const PROVIDER_DETAILS = {
  stripe:    { name: "Card (Stripe)",       blurb: "Auto-renews monthly. Visa, Mastercard, AMEX.",        tone: "Recommended for hands-off renewal." },
  billplz:   { name: "FPX / DuitNow (Billplz)", blurb: "Malaysian bank transfer. Email reminder each cycle.", tone: "Manual renewal — you confirm each month." },
  toyyibpay: { name: "FPX (ToyyibPay)",     blurb: "Lowest fees for Malaysian bank transfer.",            tone: "Manual renewal — you confirm each month." },
  invoice:   { name: "Manual Invoice",      blurb: "We email you a tax invoice. Pay via bank transfer.",  tone: "Pro tier only." },
};

function Bar({ used, cap, accent = "blue", graceBanner = false }) {
  const unlimited = cap === -1 || cap === null || cap === undefined;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, cap)) * 100));
  const over = !unlimited && used >= cap;
  const colour = over ? "bg-rose-500" : (pct > 80 ? "bg-amber-500" : `bg-${accent}-600`);
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-600 mb-1">
        <span>{used.toLocaleString()}{unlimited ? "" : ` / ${cap.toLocaleString()}`}</span>
        <span>{unlimited ? "Unlimited" : `${pct}%`}</span>
      </div>
      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${colour} transition-all`} style={{ width: unlimited ? "8%" : `${pct}%` }} />
      </div>
      {graceBanner && over && (
        <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          You're past your monthly message cap. We've started a 10-day grace window — upgrade to keep replies flowing without interruption.
        </div>
      )}
    </div>
  );
}

function GatewayPicker({ open, plan, billing, providers, onClose, onSelect, busyId }) {
  if (!open) return null;
  const eligible = providers.filter(p => p.enabled && (p.id !== "invoice" || plan?.id === "pro"));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()} data-testid="gateway-picker">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Choose payment method</h3>
            <p className="text-sm text-slate-600 mt-1">{plan?.label} — RM{billing === "yearly" ? plan?.price_myr_yearly : plan?.price_myr_monthly}/{billing === "yearly" ? "year" : "month"}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        {eligible.length === 0 ? (
          <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-4">
            No payment gateways are configured yet. Please contact support to complete your subscription.
          </div>
        ) : (
          <div className="space-y-2">
            {eligible.map(p => {
              const meta = { name: p.label || PROVIDER_DETAILS[p.id]?.name || p.id, blurb: p.note || PROVIDER_DETAILS[p.id]?.blurb || '', tone: p.auto_renew ? 'Auto-renews monthly.' : 'Manual renewal each month.' };
              const busy = busyId === p.id;
              return (
                <button key={p.id} disabled={busy} onClick={() => onSelect(p.id)} className="w-full text-left border border-slate-200 hover:border-blue-500 hover:bg-blue-50/40 rounded-xl p-4 transition disabled:opacity-60">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-slate-900">{meta.name}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{meta.blurb}</div>
                      <div className="text-xs text-slate-500 mt-1 italic">{meta.tone}</div>
                    </div>
                    {busy ? <Loader2 className="w-5 h-5 animate-spin text-blue-600 flex-shrink-0" /> : <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        <p className="text-xs text-slate-500 mt-4">Prices in MYR. You can change methods anytime from the Billing page.</p>
      </div>
    </div>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [providers, setProviders] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerPlan, setPickerPlan] = useState(null);
  const [pickerBilling, setPickerBilling] = useState("monthly");
  const [busyProvider, setBusyProvider] = useState(null);
  const [checkoutEnabled, setCheckoutEnabled] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
            const [plansRes, provRes, subRes, payRes] = await Promise.all([
        api.get('/billing/plans'),
        api.get('/billing/providers'),
        api.get('/billing/subscription'),
        api.get('/billing/payments'),
      ]);
      setPlans(plansRes.data?.plans || plansRes.data || []);
      /* Server decides. Absent on older builds, so default to open rather
         than hiding a feature that is actually working. */
      setCheckoutEnabled(plansRes.data?.checkout_enabled !== false);
      setProviders(provRes.data?.providers || provRes.data || []);
      setSubscription(subRes.data || null);
      setPayments(payRes.data?.payments || payRes.data || []);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Failed to load billing data";
      toast.error(typeof msg === "string" ? msg : "Failed to load billing data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // auto-open picker if redirected from pricing page (?plan=business&billing=monthly)
  useEffect(() => {
    const planId = params.get("plan");
    if (!planId || !plans.length) return;
    const target = plans.find(p => p.id === planId);
    if (target && target.id !== "free") {
      setPickerPlan(target);
      setPickerBilling(params.get("billing") === "yearly" ? "yearly" : "monthly");
      setPickerOpen(true);
      // clean URL
      params.delete("plan");
      params.delete("billing");
      setParams(params, { replace: true });
    }
  }, [plans, params, setParams]);

  const startCheckout = async (providerId) => {
    if (!pickerPlan) return;
    if (!checkoutEnabled) {
      toast.error("Paid plans are not open for purchase yet.");
      return;
    }
    setBusyProvider(providerId);
    try {
            const res = await api.post('/billing/checkout', {
        plan_id: pickerPlan.id,
        provider: providerId,
        billing_cycle: pickerBilling,
      });
      const data = res.data || {};
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      if (data.status === "pending_invoice") {
        toast.success("Invoice request received. Our team will email you within 1 business day.");
        setPickerOpen(false);
        await load();
        return;
      }
      toast.error("Unexpected checkout response.");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Checkout failed";
      toast.error(typeof msg === "string" ? msg : "Checkout failed");
    } finally {
      setBusyProvider(null);
    }
  };

  const openPicker = (plan) => {
    if (plan.id === "free") {
      toast.info("You're already on the free plan. Upgrade any time.");
      return;
    }
    setPickerPlan(plan);
    setPickerBilling("monthly");
    setPickerOpen(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  const currentPlan = subscription?.plan || plans.find(p => p.id === (subscription?.plan_id || "free")) || plans[0];
  const usage = subscription?.usage || {};

  return (
    <div className="space-y-6" data-testid="billing-page">
      <PageHeader title="Billing" subtitle="Manage your plan, payments, and usage limits." />

      {!checkoutEnabled && (
        <div
          data-testid="checkout-closed-notice"
          className="rounded-[14px] bg-b2-attentionwash border border-b2-attention/25 px-5 py-4"
        >
          <p className="text-[14px] font-semibold text-b2-attention">
            Paid plans are not open for purchase yet
          </p>
          <p className="mt-1 text-[13.5px] text-b2-inksoft">
            We are finalising the pricing. Your free plan keeps working as normal,
            and nothing will be charged. If you need more than the free plan covers,
            talk to us and we will arrange it directly.
          </p>
          <Link to="/contact-us" className="mt-2 inline-block text-[13.5px] font-medium text-b2-indigo hover:underline">
            Talk to us →
          </Link>
        </div>
      )}

      {/* Current plan */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Current plan</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{currentPlan?.label || "Free"}</div>
            <div className="text-sm text-slate-600 mt-1">
              {subscription?.status === "active" ? (
                <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-4 h-4" /> Active</span>
              ) : subscription?.status === "past_due" ? (
                <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-4 h-4" /> Past due</span>
              ) : (
                <span className="text-slate-500">No active subscription</span>
              )}
              {subscription?.renews_at && <span className="ml-3 text-slate-500">Renews {new Date(subscription.renews_at).toLocaleDateString()}</span>}
            </div>
          </div>
          <button onClick={() => navigate("/pricing")} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
            <Zap className="w-4 h-4" /> Change plan
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-6 pt-6 border-t border-slate-100">
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Chatbots</div>
            <Bar used={usage.chatbots_used ?? 0} cap={currentPlan?.max_chatbots ?? 1} accent="blue" />
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Messages this month</div>
            <Bar used={usage.messages_this_month ?? 0} cap={currentPlan?.monthly_messages ?? 100} accent="violet" graceBanner />
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Team members</div>
            <Bar used={usage.team_members ?? 1} cap={1} accent="emerald" />
          </div>
        </div>
      </div>

      {/* Available plans */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-1">Available plans</h3>
        <p className="text-sm text-slate-600 mb-5">Upgrade or downgrade any time. Prorated automatically.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map(p => {
            const isCurrent = currentPlan?.id === p.id;
            return (
              <div key={p.id} className={`rounded-xl border p-4 ${isCurrent ? "border-blue-500 bg-blue-50/30" : "border-slate-200"}`}>
                <div className="flex items-center justify-between">
                  <div className="font-bold text-slate-900">{p.label}</div>
                  {isCurrent && <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">Current</span>}
                </div>
                <div className="text-xl font-extrabold text-slate-900 mt-2">RM{p.price_myr_monthly}<span className="text-sm font-normal text-slate-500">/mo</span></div>
                <div className="text-xs text-slate-600 mt-2 space-y-1">
                  <div>{p.max_chatbots === -1 ? "Unlimited" : p.max_chatbots} chatbots</div>
                  <div>{(p.monthly_messages ?? 0).toLocaleString()} messages/mo</div>
                </div>
                <button disabled={isCurrent} onClick={() => openPicker(p)} className={`w-full mt-4 py-2 rounded-lg text-sm font-semibold transition ${isCurrent ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                  {isCurrent ? "Current plan" : (p.id === "free" ? "Downgrade" : "Choose")}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment history */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Receipt className="w-5 h-5 text-slate-700" />
          <h3 className="text-lg font-bold text-slate-900">Payment history</h3>
        </div>
        {payments.length === 0 ? (
          <div className="text-sm text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-6 text-center">
            No payments yet. Your invoices will appear here once you upgrade.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr><th className="text-left py-2 font-semibold">Date</th><th className="text-left font-semibold">Plan</th><th className="text-left font-semibold">Method</th><th className="text-right font-semibold">Amount</th><th className="text-right font-semibold">Status</th></tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id} className="border-b border-slate-100">
                    <td className="py-2 text-slate-700">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                    <td className="text-slate-700">{p.plan_id || "—"}</td>
                    <td className="text-slate-700">{PROVIDER_DETAILS[p.provider]?.name || p.provider || "—"}</td>
                    <td className="text-right text-slate-900 font-semibold">RM{p.amount ?? "—"}</td>
                    <td className="text-right">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${p.status === "paid" ? "bg-emerald-100 text-emerald-700" : p.status === "failed" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-700"}`}>{p.status || "pending"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <GatewayPicker open={pickerOpen} plan={pickerPlan} billing={pickerBilling} providers={providers} busyId={busyProvider} onClose={() => setPickerOpen(false)} onSelect={startCheckout} />
    </div>
  );
}
